import { useEffect, useState, type CSSProperties } from 'react';
import { chooseSpoils, type NightRun, type Profile } from '../../../campaign';
import { t } from '../../../i18n';
import { sounds } from '../../../app/sound';
import { PawnFigure } from '../PawnFigure';
import { PendingPicker, useTaker, type Apply } from '../NightCounter';
import { BrassHud, NightLabel } from './Hud';
import { NightIcon } from './NightIcon';
import { nightLabel } from './runText';
import { VelvetTray } from './Velvet';

/**
 * After a High Table won (docs/LONG_NIGHT_ART_DIRECTION.md §11.14), in this order, each with its
 * own beat: the reclaim (your former pawns slide back across the board from his side to yours,
 * each shard smoothing into an orb as it crosses the middle, its tag rewritten in your hand, a gold
 * seam drawn across it); the recruit (a new pawn rises from the smoke and is given its tag); then
 * the Spoils, three things on velvet, of which you take one.
 */

type Beat = 'reclaim' | 'recruit' | 'spoils';

export function SpoilsScene({
  profile,
  run,
  apply,
  reduceMotion,
}: {
  profile: Profile;
  run: NightRun;
  apply: Apply;
  reduceMotion: boolean;
}) {
  const last = run.outcomes[run.outcomes.length - 1];
  const reclaimed = last?.reclaimed ?? [];
  const recruited = last?.recruited ?? [];
  const beats: Beat[] = [
    ...(reclaimed.length > 0 ? (['reclaim'] as const) : []),
    ...(recruited.length > 0 ? (['recruit'] as const) : []),
    'spoils',
  ];
  const [at, setAt] = useState(reduceMotion ? beats.length - 1 : 0);
  const beat = beats[Math.min(at, beats.length - 1)]!;
  const { pending, setPending, take } = useTaker(apply);
  const name = (id: string) => (profile.pawns[id]?.name ?? id).split(' ')[0]!;

  useEffect(() => {
    if (beat === 'spoils') return;
    sounds.play(beat === 'reclaim' ? 'enso' : 'nib');
    const ms = beat === 'reclaim' ? 2200 + reclaimed.length * 180 : 2000;
    const id = setTimeout(() => setAt((a) => a + 1), ms);
    return () => clearTimeout(id);
  }, [beat, reclaimed.length]);

  return (
    <div className={`ln-screen sp-scene has-dock beat-${beat}`}>
      <header className="nb-head">
        <NightLabel night={run.night}>
          {nightLabel(run.night)} · {t('night.afterHigh')}
        </NightLabel>
        <BrassHud brass={run.brass} reduceMotion={reduceMotion} />
      </header>
      <h1 className="sp-title">
        {beat === 'reclaim'
          ? t('spoils.reclaim')
          : beat === 'recruit'
            ? t('spoils.recruit')
            : t('night.spoils')}
      </h1>

      {beat === 'reclaim' && (
        <Crossing ids={reclaimed} name={name} onSkip={() => setAt((a) => a + 1)} />
      )}

      {beat === 'recruit' && (
        <div className="sp-recruits" onClick={() => setAt((a) => a + 1)}>
          {recruited.map((id, i) => (
            <span key={id} className="sp-recruit" style={{ '--d': i } as CSSProperties}>
              <span className="sp-figure">
                <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
              </span>
              <span className="fl-tag sp-write">{name(id)}</span>
            </span>
          ))}
        </div>
      )}

      {beat === 'spoils' && (
        <>
          <p className="sp-lead">{t('night.spoilsHint')}</p>
          <VelvetTray
            run={run}
            offers={(run.spoils ?? []).map((offer) => ({ offer }))}
            actionLabel={() => t('night.take')}
            onTake={(i) => {
              const o = run.spoils![i]!;
              sounds.play('place');
              take(o, (target) => (p) => chooseSpoils(p, i, target));
            }}
          />
        </>
      )}

      <div className="ln-dock">
        {beat === 'spoils' ? (
          <button
            type="button"
            className="btn btn-large ln-dock-main"
            onClick={() => apply((p) => chooseSpoils(p, null))}
          >
            {t('night.takeNothing')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-large ln-dock-main"
            onClick={() => setAt((a) => a + 1)}
          >
            {t('action.next')}
            <NightIcon name="arrow-right" size={18} />
          </button>
        )}
      </div>
      <PendingPicker
        profile={profile}
        run={run}
        pending={pending}
        apply={apply}
        onClose={() => setPending(null)}
      />
    </div>
  );
}

/**
 * Your pawns coming home across the board from his side to yours (§11.14, §11.18): as each crosses
 * the middle its shard smooths into an orb, its tag is rewritten in your hand, and a gold seam is
 * drawn across it.
 */
export function Crossing({
  ids,
  name,
  onSkip,
}: {
  ids: readonly string[];
  name: (id: string) => string;
  onSkip?: () => void;
}) {
  return (
    // The board's two halves: his above the line, yours below.
    <div className="sp-crossing" onClick={onSkip}>
      <span className="sp-line" aria-hidden="true" />
      {ids.map((id, i) => (
        <span key={id} className="sp-pawn" style={{ '--d': i } as CSSProperties}>
          <span className="sp-figure">
            <span className="sp-shard">
              <PawnFigure side="N" crowned={false} immune={false} inscription={null} />
            </span>
            <span className="sp-orb">
              <PawnFigure side="S" crowned={false} immune={false} inscription={null} />
            </span>
            <span className="sp-seam" aria-hidden="true" />
          </span>
          <span className="sp-tag">
            <s className="sp-tag-his">{name(id)}</s>
            <span className="sp-tag-yours">{name(id)}</span>
          </span>
        </span>
      ))}
    </div>
  );
}
